import type { SpecType } from '@local/lib';

import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Transition,
} from '@mantine/core';
import { useDisclosure, useInputState } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';

import type { QualityAdjustmentTemplate } from './templateTypes';

import { ConfirmationModal, PageMeta, QualityAdjustmentsNav } from '../../components';
import {
  showErrorNotification,
  showSuccessNotification,
  showValidationErrorNotification,
} from '../../utils/notifications';
import { FormulaTestingPanel } from '../quality-formulas/components/FormulaTestingPanel';
import { QualityResultsSection } from '../quality-formulas/components/QualityResultsSection';
import { QualitySpecCard } from '../quality-formulas/components/QualitySpecCard';
import { createInitialSpecStates } from '../quality-formulas/qualityFormulaDefaults';
import { SPEC_TYPES } from '../quality-formulas/qualityFormulaMetadata';
import { validateEnabledSpecStates } from '../quality-formulas/qualityFormulaValidation';
import {
  QualityFormulaStoreProvider,
  useQualityFormulaStore,
  useQualityFormulaStoreApi,
} from '../quality-formulas/store/QualityFormulaStoreProvider';
import { usePermissions } from './hooks/usePermissions';
import { useTemplate, useTemplateActions } from './hooks/useTemplates';
import { fromTemplateConfig, toTemplateConfig } from './templateConfigConversion';

const SPEC_OPTIONS = SPEC_TYPES.map(spec => ({ label: spec.label, value: spec.key }));

// Builds the full SPEC_TYPES-keyed state the quality-formula store expects, seeding it with an
// existing template's saved single-spec config (edit mode) — converted from the API's persisted
// `ITemplateConfig` shape back into this page's form state (see `fromTemplateConfig`) — or leaving
// every spec at its default, disabled state (create mode).
const buildInitialWorkspaceState = (template?: QualityAdjustmentTemplate) => {
  const specStates = createInitialSpecStates();
  const visibleSpecTypes: SpecType[] = template ? [template.specType] : [];

  if (template) {
    specStates[template.specType] = { ...fromTemplateConfig(template.config), collapsed: false, enabled: true };
  }

  return { specStates, visibleSpecTypes };
};

interface TemplateEditorFormProps {
  template?: QualityAdjustmentTemplate;
}

// Split out from `TemplateEditorPage` so it only ever mounts once the template it's editing (if
// any) has finished loading — the quality-formula store's initial state is seeded once, by the
// enclosing `QualityFormulaStoreProvider`, so this component must not render before that data is
// ready.
const TemplateEditorForm: React.FC<TemplateEditorFormProps> = ({ template }) => {
  const navigate = useNavigate();
  const { canManageTemplates } = usePermissions();
  const { createTemplate, updateTemplate } = useTemplateActions();
  const [name, setName] = useInputState(template?.name ?? '');
  const [description, setDescription] = useInputState(template?.description ?? '');
  const [isSaving, { close: stopSaving, open: startSaving }] = useDisclosure(false);
  // Tracks whether the form or the quality-formula store has changed since mount, so Cancel and a
  // browser tab close can both warn before discarding progress. Name/description are flagged
  // directly by their own onChange (local component state); any store mutation (selecting a spec,
  // editing its formula rows, etc.) is caught by subscribing to the store itself below.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCancelConfirmOpen, { close: closeCancelConfirm, open: openCancelConfirm }] = useDisclosure(false);

  const { expandSpecs, handleVisibleSpecTypesChange, visibleSpecTypes } = useQualityFormulaStore(
    useShallow(store => ({
      expandSpecs: store.expandSpecs,
      handleVisibleSpecTypesChange: store.handleVisibleSpecTypesChange,
      visibleSpecTypes: store.visibleSpecTypes,
    })),
  );
  // Read via the store API (not `useQualityFormulaStore`) so this component isn't subscribed to —
  // and doesn't re-render on — every `specStates` change; `handleSave` only needs a snapshot at
  // submit time, not live state to render from.
  const storeApi = useQualityFormulaStoreApi();

  // Marks the form dirty only on a mutation to what `handleSave` actually persists — this spec's
  // own formula config (`specStates`) and which spec is selected (`visibleSpecTypes`) — not the
  // Formula Testing panel's sample values or preview results (`testValuesSlice`/`runPreviewSlice`),
  // which are explicitly never saved (see FormulaTestingPanel's own description text). The plain
  // vanilla store's `subscribe` always hands the listener `(state, prevState)`, so comparing just
  // these two references is enough without pulling in a selector-subscription middleware. The
  // initial seeding happens before this subscription exists, so it only ever fires on genuine
  // user changes.
  useEffect(
    () =>
      storeApi.subscribe((state, prevState) => {
        if (state.specStates !== prevState.specStates || state.visibleSpecTypes !== prevState.visibleSpecTypes) {
          setHasUnsavedChanges(true);
        }
      }),
    [storeApi],
  );

  // Warns on an actual tab close/reload, not just navigating away within the app (Cancel/Save
  // handle that themselves) — only attached while there's something to lose.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // A template belongs to exactly one Quality Specification, so at most one entry of
  // `visibleSpecTypes` is ever meaningful here — `handleVisibleSpecTypesChange` already knows how
  // to enable one spec and disable whatever was selected before. `.at(0)` (rather than `[0]`) keeps
  // this properly typed as possibly-undefined even without the project's tsconfig enabling
  // `noUncheckedIndexedAccess`.
  const selectedSpecType = visibleSpecTypes.at(0) ?? null;
  const selectedSpec = selectedSpecType ? SPEC_TYPES.find(spec => spec.key === selectedSpecType) : undefined;

  let pageTitle = 'View Template';
  if (canManageTemplates) {
    pageTitle = template ? 'Edit Template' : 'Create Template';
  }

  const handleSpecChange = (value: null | string) => {
    // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
    handleVisibleSpecTypesChange(value ? [value as SpecType] : []);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
    if (value) expandSpecs([value as SpecType]);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      openCancelConfirm();
      return;
    }
    void navigate('/templates');
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showErrorNotification(new Error('Template name is required'), 'Unable to save template');
      return;
    }
    if (!selectedSpecType) {
      showValidationErrorNotification('Cannot save template.', ['Select a Quality Specification.']);
      return;
    }

    // Same business-rule checks "Test This Formula" runs (zero rates, overlapping ranges, etc.) —
    // the shared Zod schema is still the actual source of truth (see `toTemplateConfig`'s POST/PUT),
    // but failing fast here gives a precise, field-level message instead of a generic API rejection.
    const { specStates } = storeApi.getState();
    const missingFields = validateEnabledSpecStates(specStates);
    if (missingFields.length > 0) {
      showValidationErrorNotification(
        'Cannot save template.',
        missingFields.map(field => `${field.fieldLabel}: ${field.reason}`),
      );
      return;
    }

    // Only the fields that define a formula are persisted — `collapsed`/`enabled` are this page's
    // own UI state, not part of what the template means (mirrors the shape TemplateSpecConfig
    // documents in templateTypes.ts). Converted to the API's persisted `ITemplateConfig` shape
    // (see `toTemplateConfig`) so it's validated against the same formula schemas the
    // quality-adjustments calculate endpoint uses.
    const { collapsed: _collapsed, enabled: _enabled, ...specConfig } = specStates[selectedSpecType];
    const config = toTemplateConfig(specConfig, selectedSpecType);
    const trimmedDescription = description.trim();

    startSaving();
    try {
      const input = {
        config,
        description: trimmedDescription === '' ? undefined : trimmedDescription,
        name: trimmedName,
        specType: selectedSpecType,
      };
      if (template) {
        await updateTemplate(template.id, input);
        showSuccessNotification(`Saved changes to "${trimmedName}".`);
      } else {
        await createTemplate(input);
        showSuccessNotification(`Created template "${trimmedName}".`);
      }
      void navigate('/templates');
    } catch (error) {
      showErrorNotification(error, 'Unable to save template');
    } finally {
      stopSaving();
    }
  };

  return (
    <Box maw={1180} mx="auto" pb="xl" pt="sm" px="md" w="100%">
      <PageMeta title={template ? `Edit ${template.name}` : 'New Template'} />
      <Stack gap="lg">
        <QualityAdjustmentsNav />

        <Group align="flex-start" justify="space-between" wrap="wrap">
          <Stack gap={4} maw={640}>
            <Title fz={{ base: 22, sm: 28 }} order={1}>
              {pageTitle}
            </Title>
            <Text c="dimmed" size="sm">
              Templates provide default Quality Adjustment values contracts can load — saving changes here never affects
              a contract that already loaded this template.
            </Text>
          </Stack>
          <Group>
            <Button onClick={handleCancel} variant="default">
              {canManageTemplates ? 'Cancel' : 'Back'}
            </Button>
            {canManageTemplates ? (
              <Button
                loading={isSaving}
                onClick={() => {
                  void handleSave();
                }}
              >
                Save Template
              </Button>
            ) : null}
          </Group>
        </Group>

        {canManageTemplates ? null : (
          <Paper bg="var(--mantine-color-default-hover)" p="sm" radius="md" withBorder>
            <Text c="dimmed" fz="sm">
              You have read-only access to templates. Only Admin users can modify this configuration.
            </Text>
          </Paper>
        )}

        <fieldset disabled={!canManageTemplates} style={{ border: 'none', margin: 0, padding: 0 }}>
          <Stack gap="lg">
            <Card component="section" padding="md" radius="lg" shadow="sm" withBorder>
              <Stack gap="md">
                <Group align="flex-start" grow>
                  <TextInput
                    label="Template Name"
                    onChange={event => {
                      setHasUnsavedChanges(true);
                      setName(event);
                    }}
                    placeholder="e.g. Standard Moisture Discount"
                    required
                    value={name}
                  />
                  <Select
                    data={SPEC_OPTIONS}
                    label="Quality Specification"
                    onChange={handleSpecChange}
                    placeholder="Select a quality specification"
                    required
                    value={selectedSpecType}
                  />
                </Group>
                <Textarea
                  autosize
                  label="Description"
                  minRows={2}
                  onChange={event => {
                    setHasUnsavedChanges(true);
                    setDescription(event);
                  }}
                  placeholder="Optional business-friendly note about when to use this template"
                  value={description}
                />
              </Stack>
            </Card>

            {/* Transition stays permanently mounted (rather than only rendering once `selectedSpec`
                is set) so its `mounted` prop can flip false/true and actually play the "pop" enter
                animation — matches Contract Entry's own per-spec card transition (see
                QualityFormulasPage's SpecCardSlot). */}
            <Transition duration={220} mounted={Boolean(selectedSpec)} transition="pop">
              {styles =>
                selectedSpec ? (
                  <Stack aria-label="Configure the template's formula" component="section" gap="md" style={styles}>
                    <Title order={2} size="h4">
                      Formula Configuration
                    </Title>
                    <Box id={`spec-card-${selectedSpec.key}`}>
                      <QualitySpecCard
                        collapsible={false}
                        hint={selectedSpec.hint}
                        icon={selectedSpec.icon}
                        priceBasisLocked
                        specLabel={selectedSpec.label}
                        specType={selectedSpec.key}
                      />
                    </Box>
                  </Stack>
                ) : (
                  <Box style={styles} />
                )
              }
            </Transition>
          </Stack>
        </fieldset>

        <Transition duration={220} mounted={Boolean(selectedSpec)} transition="pop">
          {styles =>
            selectedSpec ? (
              <Box style={styles}>
                <FormulaTestingPanel
                  description="Enter sample Contracted Value, Actual Value, and Product Price to see how this template's formula would adjust price and quantity — nothing here is saved."
                  enabledSpecs={[selectedSpec]}
                  testButtonLabel="Test This Formula"
                  title="Formula Testing"
                />
              </Box>
            ) : (
              <Box style={styles} />
            )
          }
        </Transition>

        <QualityResultsSection />
      </Stack>

      <ConfirmationModal
        confirmText="Discard"
        onCancel={closeCancelConfirm}
        onConfirm={() => {
          closeCancelConfirm();
          void navigate('/templates');
        }}
        opened={isCancelConfirmOpen}
        title="Discard unsaved changes?"
      >
        <Text size="sm">
          You have unsaved changes to this template. Leaving now will discard them — this can’t be undone.
        </Text>
      </ConfirmationModal>
    </Box>
  );
};

/**
 * Route shell for both `/templates/new` and `/templates/:id/edit` — waits for the existing
 * template (edit mode) to finish loading, then mounts a `QualityFormulaStoreProvider` (seeded
 * from that template's saved config, if any) keyed by the template id, so switching between
 * templates always starts from a fresh store instance instead of reusing state from whichever
 * template was previously open.
 */
export const TemplateEditorPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { data: template, isFetching, isLoading } = useTemplate(id ?? null);

  // `isLoading` alone only covers the very first fetch — `useTemplate`'s mandatory
  // `refetchOnMount: 'always'` refetch on every visit still reports cached (possibly stale) data
  // with `isLoading: false` while that refetch is in flight. `TemplateEditorForm` only ever reads
  // `template` once, at mount, so it must not mount until that refetch has actually settled.
  if (id && (isLoading || isFetching)) {
    return (
      <Box maw={1180} mx="auto" pb="xl" pt="sm" px="md" w="100%">
        <QualityAdjustmentsNav />
        <Group justify="center" p="xl">
          <Loader />
        </Group>
      </Box>
    );
  }

  const resolvedTemplate = id ? template : undefined;
  const initialState = buildInitialWorkspaceState(resolvedTemplate);

  return (
    <QualityFormulaStoreProvider
      initialSpecStates={initialState.specStates}
      initialVisibleSpecTypes={initialState.visibleSpecTypes}
      key={id ?? 'new'}
    >
      <TemplateEditorForm template={resolvedTemplate} />
    </QualityFormulaStoreProvider>
  );
};
